import React, { useState } from 'react';
import {
  Alert,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  ExclamationCircleIcon,
  FileAltIcon,
  ListIcon,
  LockIcon,
  PlayIcon,
  SyncAltIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useAdminSplaUsage } from '../../../hooks/useAdminSplaUsage';
import { formatMonthYear, formatWallClockDateTime } from '../../../utils/dateFormat';
import type { SplaReport } from '../../../types/models/Spla';
import SplaUsageMonthDetail from './SplaUsageMonthDetail';
import SplaGenerateReportModal from './SplaGenerateReportModal';

interface SplaUsageTabProps {
  /** Defers every admin fetch until the page's staff check has passed. */
  isStaff: boolean;
}

/** Frozen (sent, immutable forever) vs open (may still be regenerated). */
export function ReportStatusLabel({ report }: { report: SplaReport }) {
  return report.frozen ? (
    <Tooltip content="Sent to the distributor — this month is immutable forever (the legal record).">
      <Label color="grey" icon={<LockIcon />} isCompact>
        Frozen
      </Label>
    </Tooltip>
  ) : (
    <Label color="green" isCompact>
      Open
    </Label>
  );
}

/**
 * The monthly reporting layer — a registry of snapshot months, newest first. Deliberately offers NO
 * edit or delete anywhere: corrections go through the Consumption tab followed by a regeneration,
 * and a frozen month is the legal record of what was mailed to the distributor.
 */
function SplaUsageTab({ isStaff }: SplaUsageTabProps) {
  const { reports, loading, error, refetch } = useAdminSplaUsage(isStaff);

  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  // Remount key for the drill-down: bumped when a regeneration touches the month being viewed.
  const [detailVersion, setDetailVersion] = useState(0);

  const handleGenerated = (report: SplaReport) => {
    void refetch();
    if (selectedReportId === report.id) {
      setDetailVersion((v) => v + 1);
    }
  };

  // In-tab drill-down (list → detail with a back link — too much data for a modal).
  if (selectedReportId !== null) {
    return (
      <SplaUsageMonthDetail
        key={`${selectedReportId}-${detailVersion}`}
        isStaff={isStaff}
        reportId={selectedReportId}
        onBack={() => setSelectedReportId(null)}
      />
    );
  }

  const renderFileCell = (report: SplaReport) => {
    if (report.file_path === null && report.file_sha256 === null) {
      return '';
    }
    return (
      <Tooltip
        content={
          <>
            {report.file_path !== null && <div>{report.file_path}</div>}
            {report.file_sha256 !== null && <div>SHA-256: {report.file_sha256}</div>}
            <div>The workbook archive lives on the host — there is no download endpoint.</div>
          </>
        }
      >
        <Icon aria-label="Archived workbook">
          <FileAltIcon />
        </Icon>
      </Tooltip>
    );
  };

  const renderContent = () => {
    if (loading && reports.length === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load the usage reports"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (reports.length === 0) {
      return (
        <EmptyState titleText="No report months yet" icon={ListIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            No monthly snapshot has been generated. Generate one to preview the workbook the distributor
            will receive.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlayIcon />} onClick={() => setIsGenerateOpen(true)}>
                Generate snapshot
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <Table aria-label="SPLA monthly usage reports" variant="compact">
        <Thead>
          <Tr>
            <Th>Month</Th>
            <Th>Status</Th>
            <Th>Generations</Th>
            <Th>Rows</Th>
            <Th>Sent</Th>
            <Th>File</Th>
            <Th screenReaderText="Row actions" />
          </Tr>
        </Thead>
        <Tbody>
          {reports.map((report) => (
            <Tr key={report.id}>
              <Td dataLabel="Month">
                <Button variant="link" isInline onClick={() => setSelectedReportId(report.id)}>
                  {formatMonthYear(report.month)}
                </Button>
              </Td>
              <Td dataLabel="Status">
                <ReportStatusLabel report={report} />
              </Td>
              <Td dataLabel="Generations">generated {report.generation}×</Td>
              <Td dataLabel="Rows">{report.row_count}</Td>
              <Td dataLabel="Sent">
                {report.frozen ? (
                  <>
                    {formatWallClockDateTime(report.sent_at)}
                    {report.sent_to !== null && <div>{report.sent_to}</div>}
                  </>
                ) : (
                  ''
                )}
              </Td>
              <Td dataLabel="File">{renderFileCell(report)}</Td>
              <Td modifier="fitContent">
                <Button variant="secondary" size="sm" onClick={() => setSelectedReportId(report.id)}>
                  View
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Alert
        variant="info"
        isInline
        isPlain
        title="Reports are immutable — there is nothing to edit or delete here. Corrections go through the Consumption tab followed by a regeneration; a frozen month is the legal record of what was sent to the distributor."
        className="pf-v6-u-mb-md"
      />
      <Toolbar>
        <ToolbarContent>
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem>
              <Button
                variant="primary"
                icon={<PlayIcon />}
                onClick={() => setIsGenerateOpen(true)}
                isDisabled={loading && reports.length === 0}
              >
                Generate snapshot
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={() => void refetch()}
                isLoading={loading}
                isDisabled={loading}
              >
                Refresh
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
      {renderContent()}
      {isGenerateOpen && (
        <SplaGenerateReportModal
          isOpen
          reports={reports}
          onClose={() => setIsGenerateOpen(false)}
          onGenerated={handleGenerated}
        />
      )}
    </>
  );
}

export default SplaUsageTab;
